import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import type * as OringApiResponses from '@/types/oring-api-responses'
import type * as OringApiRequests from '@/types/oring-api'
import {useOringCalculatorService} from '@/composables/oring_calc_service'
import {useOringUnitConverter} from '~/composables/oring/useOringUnitConverter'

export const useORingCalculatorStore = defineStore('ORingCalculatorModule', () => {
  // States for all workflows
  const oringCalculatorImperial = ref<boolean>(false)
  const oringCalculatorFahrenheit = ref<boolean>(false)
  const oringCalculatorPsi = ref<boolean>(false)
  const oringSku = ref<number | null>(null)
  const oringMaterialIds = ref<number[]>([])
  const oringBookmarkedMaterialIds = ref<number[]>([]) // Stores booked material IDs for the O-Ring Design Cockpit from material assistant
  const oringPdfData = ref<OringApiRequests.ORingConfiguratorDownloadPdfRequest | null>(null)
  const housingExpertModeResult = ref<OringApiResponses.ORingCreateHousingExpertModeResponse | null>(null)
  const unitSwitchingInProgress = ref<boolean>(false)
  const globalLoading = ref<boolean>(false)

  // Persistent filters for O-Ring Material Selection (to keep state when navigating back)
  const oringMaterialSelectionElastomerFilter = ref<{text: string; value: string | null} | null>(null)
  const oringMaterialSelectionShoreHardnessFilter = ref<{text: string; value: string | null} | null>(null)
  const oringMaterialSelectionCompoundFilter = ref<{text: string; value: string | null} | null>(null)

  // States for tolerance data - stores tolerance class IDs by parameter key
  const toleranceData = ref<Record<string, string | null>>({})
  // States for parameter input values - stores actual parameter values by parameter key  
  const parameterInputData = ref<Record<string, number | null>>({})
  // States for free tolerance numeric values (persist user-entered free tolerances)
  const freeToleranceValues = ref<Record<string, { value?: number | null; min?: number | null; max?: number | null }>>({})
  const toleranceClasses = ref<OringApiResponses.ToleranceClass[]>([])
  const toleranceClassesByCategory = ref<Map<number, OringApiResponses.ToleranceClass[]>>(new Map())

  function buildToleranceClassesByCategory() {
    const map = new Map<number, OringApiResponses.ToleranceClass[]>()
    for (const tol of toleranceClasses.value) {
      if (!map.has(tol.toleranceCategoryId)) {
        map.set(tol.toleranceCategoryId, [])
      }
      map.get(tol.toleranceCategoryId)?.push(tol)
    }
    toleranceClassesByCategory.value = map
  }
  const oringConstructionViewCurrentHousing = ref<OringApiResponses.Housing | null>(null)
  const oringIsAssistantDetails = ref<boolean>(true)
  const oringDetailsD1 = ref<number | null>(null)
  const oringDetailsD2 = ref<number | null>(null)

  // Step state for O-Ring Assistant
  const oringAssistantCurrentStep = ref<number>(1)
  const oringAssistantCompletedSteps = ref<number[]>([])

  // States for the O-Ring Assistant
  const oringAssistantCurrentHousing = ref<OringApiResponses.Housing | null>(null)
  const oringAssistantOptimalORing = ref<OringApiResponses.ORingCalculateOptimalORingAssistantResponse | null>(null)
  const oringAssistantConstructionView = ref<OringApiResponses.ORingGetConstructionViewDetailsConfiguratorResponse | null>(null)
  const oringAssistantIsHydraulic = ref<boolean>(true)
  const oringAssistantIsRadial = ref<boolean>(true)
  const oringAssistantIsDynamic = ref<boolean>(false)
  const oringAssistantGrooveInShaft = ref<boolean>(true)
  const oringAssistantOnShaft = ref<boolean>(false)

  // Step state for O-Ring Design Space Assistant
  const oringDesignSpaceCurrentStep = ref<number>(1)
  const oringDesignSpaceCompletedSteps = ref<number[]>([])

  // States for the O-Ring Design Space
  const oringDesignSpaceCurrentHousing = ref<OringApiResponses.Housing | null>(null)
  const oringDesignSpaceSku = ref<number | null>(null)
  const oringDesignSpaceD1 = ref<number | null>(null)
  const oringDesignSpaceD2 = ref<number | null>(null)

  // States for the O-Ring Design Cockpit
  const oringDesignCockpitCurrentHousing = ref<OringApiResponses.ExpertHousing | null>(null)
  const oringDesignCockpitCalculateResult = ref<OringApiResponses.ORingCalculateResultExpertModeResponse | null>(null)
  const oringDesignCockpitIsHydraulic = ref<boolean>(true)
  const oringDesignCockpitIsRadial = ref<boolean>(true)
  const oringDesignCockpitIsDynamic = ref<boolean>(false)
  const oringDesignCockpitGrooveInShaft = ref<boolean>(true)
  const oringDesignCockpitOnShaft = ref<boolean>(true)
  const oringDesignCockpitDefaultTolerances = ref<{
    parameterTranslationKey: string
    defaultToleranceClassId: number
    toleranceCategoryId: number
    geometryId: number
  }[] | null>(null)
  // Step state for Design Cockpit
  const designCockpitCurrentStep = ref<number>(1)
  const designCockpitCompletedSteps = ref<number[]>([])

  // States for the O-Ring Material Assistant
  const oringMaterialAssistantIsDynamic = ref<boolean>(false)
  // Step state for Material Assistant
  const materialAssistantCurrentStep = ref<number>(1)
  const materialAssistantCompletedSteps = ref<number[]>([])
  // Material assistant specific states
  const oringMaterialAssistantMaterialIds = ref<number[]>([])
  const loadingFluids = ref<boolean>(false)

  // Fluid options for Material Assistant step1 (store full FluidItem objects)
  const oringMaterialAssistantFluidOptions = ref<OringApiResponses.FluidItem[]>([])
  // Track last loaded language for fluids to avoid unnecessary API calls
  const lastLoadedFluidsLanguage = ref<string | null>(null)
  // Metric ranges for Material Assistant step3
  const oringMaterialAssistantMetricRanges = ref<OringApiResponses.ORingGetMetricRangesMaterialAssistantResponse|null>(null)
  // Step 3: Calculation parameter values
  const oringMaterialAssistantGapWidth = ref<number | null>(0.01)
  const oringMaterialAssistantPressure = ref<number>(5)

  /**
   * Converts oringMaterialAssistantPressure between bar and psi
   * @param imperial If true, convert bar to psi. If false, convert psi to bar.
   * @returns Converted pressure value
   */
  function convertMaterialAssistantPressure(imperial: boolean): void {
    const multiplicator = 14.5037738
    const value = oringMaterialAssistantPressure.value
    if (value === null || value === undefined) {return}
    // If imperial, convert bar to psi
    // If metric, convert psi to bar
    oringMaterialAssistantPressure.value = imperial
      ? parseFloat((value * multiplicator).toFixed(1)) // bar to psi
      : parseFloat((value / multiplicator).toFixed(1)) // psi to bar
  }
  // Step 2: Temperature values
  const materialAssistantMinTemp = ref<number | null>(-10)
  const materialAssistantMaxTemp = ref<number | null>(80)
  // Selected fluids for Material Assistant step1 (store fluidIds)
  const oringMaterialAssistantFluidIds = ref<number[]>([])

  // Track the source page that navigates to material assistant
  const materialAssistantSourcePage = ref<'design-cockpit' | 'oring-assistant' | null>(null)

  const currentLanguage = computed(() => useNuxtApp().$i18n.locale.value)

  const oringAssistantApplicationId = computed<number>(() => {
    if (oringAssistantIsHydraulic.value) {
      return oringAssistantIsDynamic.value && oringAssistantIsRadial.value ? 2 : 1
    } else {
      return oringAssistantIsDynamic.value && oringAssistantIsRadial.value ? 5 : 4
    }
  })

  const oringDesignCockpitApplicationId = computed<number>(() => {
    if (oringDesignCockpitIsHydraulic.value) {
      return oringDesignCockpitIsDynamic.value && oringDesignCockpitIsRadial.value ? 2 : 1
    } else {
      return oringDesignCockpitIsDynamic.value && oringDesignCockpitIsRadial.value ? 5 : 4
    }
  })

  const oringAssistantGeometryId = computed<number>(() => {
    if (oringAssistantIsRadial.value) {
      return oringAssistantGrooveInShaft.value ? 9 : 8
    } else {
      return 12
    }
  })

  const oringDesignCockpitGeometryId = computed<number>(() => {
    if (oringDesignCockpitIsRadial.value) {
      return oringDesignCockpitGrooveInShaft.value ? 9 : 8
    } else {
      return 12
    }
  })

  const oringDesignCockpitHasChamfer = computed<boolean>(() => {
    // show when is radial
    return oringDesignCockpitIsRadial.value
  })

  function resetDesignSpaceStates() {
    oringAssistantIsHydraulic.value = true
    oringAssistantIsRadial.value = false
    oringAssistantIsDynamic.value = false
    oringAssistantGrooveInShaft.value = true
    oringAssistantOnShaft.value = true

    oringDesignSpaceCurrentHousing.value = null
    oringDesignSpaceSku.value = null
    oringDesignSpaceD1.value = null
    oringDesignSpaceD2.value = null
    oringDesignSpaceCurrentStep.value = 1
    oringDesignSpaceCompletedSteps.value = []
  }

  function resetAssistantStates() {
    oringAssistantIsHydraulic.value = true
    oringAssistantIsRadial.value = true
    oringAssistantIsDynamic.value = false
    oringAssistantGrooveInShaft.value = true
    oringAssistantOnShaft.value = false
    oringAssistantCurrentHousing.value = null
    parameterInputData.value = {}
    oringMaterialIds.value = []
    toleranceData.value = {}

    oringDesignSpaceSku.value = null
    oringDesignSpaceD1.value = null
    oringDesignSpaceD2.value = null

    // Reset navigation state
    oringAssistantCurrentStep.value = 1
    oringAssistantCompletedSteps.value = []

    oringMaterialSelectionElastomerFilter.value = {text: '', value: null}
    oringMaterialSelectionShoreHardnessFilter.value = {text: '', value: null}
    oringMaterialSelectionCompoundFilter.value = {text: '', value: null}
  }

  function resetNavigationState() {
    oringAssistantCurrentStep.value = 1
    oringAssistantCompletedSteps.value = []
  }

  function resetDesignCockpitStates() {
    oringDesignCockpitIsHydraulic.value = true
    oringDesignCockpitIsRadial.value = true
    oringDesignCockpitIsDynamic.value = false
    oringDesignCockpitGrooveInShaft.value = true
    oringDesignCockpitOnShaft.value = true
    oringDesignCockpitCurrentHousing.value = null
    oringDesignCockpitDefaultTolerances.value = null
    designCockpitCurrentStep.value = 1
    designCockpitCompletedSteps.value = []
    toleranceData.value = {}
    parameterInputData.value = {}
    oringMaterialIds.value = []
  }

  // Initialize or update chamfer angle with default value
  function initializeChamferAngleWithDefault(defaultValue: number = 15) {
    if (!oringDesignCockpitCurrentHousing.value) {
      return
    }

    // Initialize chamferAngle if it doesn't exist or has no value
    if (!oringDesignCockpitCurrentHousing.value.chamferAngle ||
        oringDesignCockpitCurrentHousing.value.chamferAngle.parameterValue === 0) {
      oringDesignCockpitCurrentHousing.value.chamferAngle = {
        toleranceClassId: 1,
        toleranceClassName: null,
        parameterValue: defaultValue,
        val: defaultValue,
        roundedVal: defaultValue,
        toleranceMin: 0,
        toleranceMax: 0,
        parameterMin: 0,
        parameterMax: 0,
        isEqual: false
      }
    }
  }
  function resetMaterialAssistantStates() {
    materialAssistantCurrentStep.value = 1
    materialAssistantCompletedSteps.value = []
    oringMaterialAssistantFluidIds.value = []
    oringMaterialAssistantMaterialIds.value = []
    oringMaterialAssistantFluidOptions.value = []
    oringMaterialAssistantMetricRanges.value = null
    materialAssistantMinTemp.value = -10
    materialAssistantMaxTemp.value = 80
    oringMaterialAssistantIsDynamic.value = false
    oringMaterialAssistantGapWidth.value = 0.01
    oringMaterialAssistantPressure.value = 5
  }

  function navigateToMaterialAssistant(sourcePage: 'design-cockpit' | 'oring-assistant') {
    materialAssistantSourcePage.value = sourcePage
    resetMaterialAssistantStates()
  }

  function oringAssistantSearchParameters(): OringApiRequests.ORingSearchParameter[] {
    switch (oringAssistantGeometryId.value) {
    case 8: // Radial, Static - holding
      return [
        {searchParameterKey: 'd5', searchValue: oringAssistantCurrentHousing.value?.d5?.val},
        {searchParameterKey: 'd10', searchValue: oringAssistantCurrentHousing.value?.d10?.val},
        {searchParameterKey: 'd6', searchValue: oringAssistantCurrentHousing.value?.d6?.val},
        {searchParameterKey: 'b1in', searchValue: oringAssistantCurrentHousing.value?.b1?.val}
      ]
    case 9: // Radial, Dynamic and Groove in Shaft - stretching
      return [
        {searchParameterKey: 'd9', searchValue: oringAssistantCurrentHousing.value?.d9?.val},
        {searchParameterKey: 'd4', searchValue: oringAssistantCurrentHousing.value?.d4?.val},
        {searchParameterKey: 'd3', searchValue: oringAssistantCurrentHousing.value?.d3?.val},
        {searchParameterKey: 'b1out', searchValue: oringAssistantCurrentHousing.value?.b1?.val}
      ]
    case 12: // Axial, Static - both
      return [
        {searchParameterKey: 'd8', searchValue: oringAssistantCurrentHousing.value?.d8?.val},
        {searchParameterKey: 'height', searchValue: oringAssistantCurrentHousing.value?.h?.val},
        {searchParameterKey: 'b4', searchValue: oringAssistantCurrentHousing.value?.b4?.val}
      ]
    default:
      return []
    }
  }

  function oringAssistantDShaft(): number {
    switch (oringAssistantGeometryId.value) {
    case 8: // Radial, Static
      return oringAssistantCurrentHousing.value?.d5?.val ?? 0
    case 9: // Radial, Dynamic and Groove in Shaft
      return oringAssistantCurrentHousing.value?.d3?.val ?? 0
    case 12: // Axial
      return oringAssistantCurrentHousing.value?.d8?.val ?? 0
    default:
      return 0
    }
  }

  function oringMaterialIdSearchParameters(): OringApiRequests.ORingSearchParameter[] {
    return oringMaterialIds.value.length > 0
      ? oringMaterialIds.value.map(id => ({searchParameterKey: 'materialId', searchValue: id}))
      : []
  }

  function oringAssistantCheckHousingRequest(): OringApiRequests.ORingAssistantCheckHousingRequest {
    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      housingValues: [
        {searchParameterKey: 'applicationID', searchValue: oringAssistantApplicationId.value},
        {searchParameterKey: 'geometryID', searchValue: oringAssistantGeometryId.value},
        {searchParameterKey: 'onShaft', searchValue: oringAssistantOnShaft.value ? 1 : 0},
        {searchParameterKey: 'chamfer', searchValue: 15},
        ...oringAssistantSearchParameters()
      ]
    }
  }

  function oringAssistantCalculateHousingRequest(): OringApiRequests.ORingAssistantCalculateHousingRequest {
    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      initialValues: [
        {searchParameterKey: 'applicationID', searchValue: oringAssistantApplicationId.value},
        {searchParameterKey: 'geometryID', searchValue: oringAssistantGeometryId.value},
        {searchParameterKey: 'mountingOnShaft', searchValue: oringAssistantOnShaft.value},
        {searchParameterKey: 'dShaft', searchValue: oringAssistantDShaft()}
      ]
    }
  }

  function oringAssistantCalculateOptimalOringRequest(): OringApiRequests.ORingAssistantCalculateOptimalORingRequest {
    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      oRingInput: [
        {searchParameterKey: 'applicationID', searchValue: oringAssistantApplicationId.value},
        {searchParameterKey: 'geometryID', searchValue: oringAssistantGeometryId.value},
        {searchParameterKey: 'onShaft', searchValue: oringAssistantOnShaft.value ? 1 : 0},
        ...oringAssistantSearchParameters(),
        ...oringMaterialIdSearchParameters()
      ]
    }
  }
  function oringAssistantDownloadPdfRequest(): OringApiRequests.ORingConfiguratorDownloadPdfRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      ringToUse: '',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringAssistantCurrentHousing.value as OringApiResponses.ExpertHousing
    }
  }

  function oringDesignCockpitDownloadPdfRequest(): OringApiRequests.ORingConfiguratorDownloadPdfRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      ringToUse: '',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringDesignCockpitCurrentHousing.value as OringApiResponses.ExpertHousing
    }
  }

  function oringDesignSpaceDownloadPdfRequest(): OringApiRequests.ORingConfiguratorDownloadPdfRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      ringToUse: 'designSpaceRing',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringDesignSpaceCurrentHousing.value as OringApiResponses.ExpertHousing
    }
  }

  function oringAssistantSendMailRequest(): OringApiRequests.ORingConfiguratorSendMailRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      mailAddress: oringPdfData.value?.pdfDownloadParameter?.eMail || '',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringAssistantCurrentHousing.value as OringApiResponses.ExpertHousing
    }
  }

  function oringDesignCockpitSendMailRequest(): OringApiRequests.ORingConfiguratorSendMailRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      mailAddress: oringPdfData.value?.pdfDownloadParameter?.eMail || '',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringDesignCockpitCurrentHousing.value
    }
  }

  function oringDesignSpaceSendMailRequest(): OringApiRequests.ORingConfiguratorSendMailRequest {
    return {
      pdfDownloadParameter: oringPdfData.value?.pdfDownloadParameter || null,
      language: oringPdfData.value?.language || currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      mailAddress: oringPdfData.value?.pdfDownloadParameter?.eMail || '',
      creationTimeIso: getFormattedIsoDateTime(),
      pdfHousing: oringDesignSpaceCurrentHousing.value as OringApiResponses.ExpertHousing
    }
  }

  function oringDesignCockpitCreateHousingRequest(): OringApiRequests.ORingExpertModeCreateHousingRequest {
    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      geometryId: oringDesignCockpitGeometryId.value,
      applicationId: oringDesignCockpitApplicationId.value,
      onShaft: oringDesignCockpitOnShaft.value,
      expertInputHousing: oringDesignCockpitCurrentHousing.value
    }
  }

  function toToleranceString(tol: OringApiResponses.ORingToleranceValue | undefined | null) {
    if (!tol) {
      return ''
    }
    const decimals = oringCalculatorImperial.value ? 4 : 3
    return JSON.stringify({
      ParameterValue: tol.parameterValue?.toFixed(decimals) ?? '',
      ToleranceClassId: tol.toleranceClassId?.toString() ?? '',
      ToleranceMin: tol.toleranceMin?.toFixed(decimals) ?? '',
      ToleranceMax: tol.toleranceMax?.toFixed(decimals) ?? '',
      IsEqual: tol.isEqual ? 'true' : 'false'
    })
  }

  function oringDesignCockpitToleranceParameters(): OringApiRequests.ORingToleranceParameter[] {
    switch (oringDesignCockpitGeometryId.value) {
    case 8: // Radial
      return [
        {toleranceParameterKey: 'd1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d1)},
        {toleranceParameterKey: 'd2', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d2)},
        {toleranceParameterKey: 'd5', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d5)},
        {toleranceParameterKey: 'd10', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d10)},
        {toleranceParameterKey: 'd6', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d6)},
        {toleranceParameterKey: 'b1in', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.b1)},
        {toleranceParameterKey: 'r1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.r1)}
      ]
    case 9: // Radial, Dynamic and Groove in Shaft
      return [
        {toleranceParameterKey: 'd1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d1)},
        {toleranceParameterKey: 'd2', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d2)},
        {toleranceParameterKey: 'd9', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d9)},
        {toleranceParameterKey: 'd4', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d4)},
        {toleranceParameterKey: 'd3', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d3)},
        {toleranceParameterKey: 'b1out', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.b1)},
        {toleranceParameterKey: 'r1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.r1)}
      ]
    case 12: // Axial
      return [
        {toleranceParameterKey: 'd1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d1)},
        {toleranceParameterKey: 'd2', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.oRing?.d2)},
        {toleranceParameterKey: 'gap', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.gapWidth)},
        {toleranceParameterKey: 'height', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.h)},
        {toleranceParameterKey: 'b4', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.b4)},
        {toleranceParameterKey: 'd8', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.d8)},
        {toleranceParameterKey: 'r1', toleranceInfoString: toToleranceString(oringDesignCockpitCurrentHousing.value?.r1)}
      ]
    default:
      return []
    }
  }

  function oringDesignCockpitCalculateResultRequest(): OringApiRequests.ORingExpertModeCalculateResultRequest {
    const searchParameters: OringApiRequests.ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: oringDesignCockpitApplicationId.value},
      {searchParameterKey: 'geometryID', searchValue: oringDesignCockpitGeometryId.value},
      {searchParameterKey: 'onShaft', searchValue: oringDesignCockpitOnShaft.value ? 1 : 0}
    ]

    //add search parameter if chamfer was shown
    if (oringDesignCockpitHasChamfer.value) {
      searchParameters.push({searchParameterKey: 'chamfer', searchValue: oringDesignCockpitCurrentHousing.value?.chamferAngle.parameterValue})
    }

    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      searchParameters,
      toleranceParameters: oringDesignCockpitToleranceParameters()
    }
  }

  async function createHousingExpertMode(): Promise<void> {
    const oringCalculatorService = useOringCalculatorService()
    // Use current language from the store
    const language = currentLanguage.value

    const request: OringApiRequests.ORingExpertModeCreateHousingRequest = {
      language,
      imperial: oringCalculatorImperial.value,
      geometryId: oringDesignCockpitGeometryId.value,
      applicationId: oringDesignCockpitApplicationId.value,
      onShaft: oringDesignCockpitOnShaft.value,
      expertInputHousing: oringDesignCockpitCurrentHousing.value
    }

    try {
      const result = await oringCalculatorService.createHousingExpertMode(request)
      housingExpertModeResult.value = result

      // Update the current housing with the housing data from the API response
      if (result?.housing) {
        oringDesignCockpitCurrentHousing.value = result.housing
        // Initialize chamfer angle with default value of 15
        initializeChamferAngleWithDefault(15)
      }
    } catch {
      // Handle error silently and set result to null
      housingExpertModeResult.value = null
      // Don't reset the housing on error to preserve existing data
    }
  }

  function oringAssistantConstructionViewRequest(): OringApiRequests.ORingConfiguratorGetConstructionViewDetailsRequest {
    const searchParameters: OringApiRequests.ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: oringAssistantApplicationId.value},
      {searchParameterKey: 'geometryID', searchValue: oringAssistantGeometryId.value},
      {searchParameterKey: 'onShaft', searchValue: oringAssistantOnShaft.value ? 1 : 0},
      {searchParameterKey: 'productId', searchValue: oringSku.value},
      {searchParameterKey: 'd1', searchValue: oringDetailsD1.value ?? 0},
      {searchParameterKey: 'd2', searchValue: oringDetailsD2.value ?? 0}
    ]

    return {
      searchParameters,
      imperial: oringCalculatorImperial.value,
      currentHousing: oringAssistantOptimalORing.value?.catalogueRings?.find(catalogueRing => catalogueRing?.catalogueRing?.articleNo === oringSku.value) ?? null
    }
  }

  function oringDesignCockpitConstructionViewRequest(): OringApiRequests.ORingConfiguratorGetConstructionViewDetailsRequest {
    const searchParameters: OringApiRequests.ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: oringDesignCockpitApplicationId.value},
      {searchParameterKey: 'geometryID', searchValue: oringDesignCockpitGeometryId.value},
      {searchParameterKey: 'onShaft', searchValue: oringDesignCockpitOnShaft.value ? 1 : 0},
      {searchParameterKey: 'productId', searchValue: oringSku.value},
      {searchParameterKey: 'd1', searchValue: oringDetailsD1.value ?? 0},
      {searchParameterKey: 'd2', searchValue: oringDetailsD2.value ?? 0}
    ]

    return {
      searchParameters,
      imperial: oringCalculatorImperial.value,
      currentHousing: oringAssistantOptimalORing.value?.catalogueRings?.find(catalogueRing => catalogueRing?.catalogueRing?.articleNo === oringSku.value) ?? null
    }
  }

  function oringDesignSpaceCalculateHousingRequest(): OringApiRequests.ORingDesignAssistantCalculateHousingRequest {
    const searchParams: OringApiRequests.ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: oringAssistantApplicationId.value},
      {searchParameterKey: 'geometryID', searchValue: oringAssistantGeometryId.value},
      {searchParameterKey: 'onShaft', searchValue: oringAssistantOnShaft.value ? 1 : 0},
      {searchParameterKey: 'd1', searchValue: oringDesignSpaceD1.value ?? 0},
      {searchParameterKey: 'd2', searchValue: oringDesignSpaceD2.value ?? 0}
    ]

    return {
      language: currentLanguage.value,
      imperial: oringCalculatorImperial.value,
      fahrenheit: oringCalculatorFahrenheit.value,
      searchParameters: searchParams
    }
  }

  const setImperial = (value: boolean) => {
    oringCalculatorImperial.value = value
  }

  function getFormattedDate() {
    const date = new Date()
    return {
      dateStr: date.toISOString().split('T')[0],
      timeStr: date.toTimeString().split(' ')[0].replace(/:/g, '-'),
      isoString: date.toISOString()
    }
  }

  function getFormattedIsoDateTime() {
    const {dateStr, timeStr} = getFormattedDate()
    return `${dateStr}T${timeStr.replace(/-/g, ':')}`
  }

  const performUnitLengthSwitch = async (imperial: boolean) => {
    unitSwitchingInProgress.value = true
    oringDesignSpaceD1.value = convertUnitLength(imperial, oringDesignSpaceD1.value)
    oringDesignSpaceD2.value = convertUnitLength(imperial, oringDesignSpaceD2.value)
    oringDetailsD1.value = convertUnitLength(imperial, oringDetailsD1.value)
    oringDetailsD2.value = convertUnitLength(imperial, oringDetailsD2.value)
    oringDesignSpaceCurrentHousing.value = convertHousingUnitLength(imperial, oringDesignSpaceCurrentHousing.value)
    oringAssistantCurrentHousing.value = convertHousingUnitLength(imperial, oringAssistantCurrentHousing.value)
    oringDesignCockpitCurrentHousing.value = convertHousingUnitLength(imperial, oringDesignCockpitCurrentHousing.value)
    oringMaterialAssistantGapWidth.value = convertUnitLength(imperial, oringMaterialAssistantGapWidth.value)
    if (oringAssistantOptimalORing.value?.assistantORing) {
      oringAssistantOptimalORing.value.assistantORing = convertHousingUnitLength(imperial, oringAssistantOptimalORing.value.assistantORing)
    }

    if (oringAssistantConstructionView.value?.expertHousing) {
      oringAssistantConstructionView.value.expertHousing = convertHousingUnitLength(imperial, oringAssistantConstructionView.value.expertHousing)
    }

    if (oringAssistantOptimalORing.value?.catalogueRings) {
      Object.keys(oringAssistantOptimalORing.value.catalogueRings).forEach(housingKey => {
        oringAssistantOptimalORing.value!.catalogueRings[housingKey] = convertHousingUnitLength(imperial, oringAssistantOptimalORing.value!.catalogueRings[housingKey])
      })
    }

    if (oringDesignCockpitCalculateResult.value?.resultRings) {
      Object.keys(oringDesignCockpitCalculateResult.value.resultRings).forEach(housingKey => {
        oringDesignCockpitCalculateResult.value!.resultRings[housingKey] = convertHousingUnitLength(imperial, oringDesignCockpitCalculateResult.value!.resultRings[housingKey])
      })
    }

    try {
      await useOringUnitConverter().performUnitSwitch(
        toleranceData.value,
        parameterInputData.value,
        (data: Record<string, string | null>) => {
          toleranceData.value = data
        },
        (data: Record<string, number | null>) => {
          parameterInputData.value = data
        },
        () => {
          // Handle validation errors - errors will be shown through the validator
        },
        () => {
          // Handle housing validation if needed
        },
        () => {
          // Handle API errors silently
        },
        imperial
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error during unit length switch:', e)
    }

    setImperial(imperial)
    unitSwitchingInProgress.value = true
  }

  const convertHousingUnitLength = (imperial: boolean, housing: OringApiResponses.Housing | OringApiResponses.ExpertHousing | null): any => {
    if (!housing) {
      return null
    }

    const convertedHousing = {...housing}
    // Keys to convert when converting housing unit length
    const housingUnitConvertKeys = [
      'val', 'parameterValue', 'roundedVal', 'toleranceMax', 'toleranceMin', 'valueMin', 'valueMean', 'valueMax', 'ra', 'rz', 'parameterMin', 'parameterMax'
    ]
    const housingUnitConvertAreaKeys = [
      // Add keys that represent area (mm² <-> in²)
      'ringSurface'
    ]
    const housingUnitConvertVolumeKeys = [
      // Add keys that represent volume (mm³ <-> in³)
      'ringVolume'
    ]
    const housingUnitConvertMicronKeys = [
      // Add keys that represent microns (µm <-> µin)
      'leadInChamfer', 'sideSurface', 'staticSealingSurface'
    ]
    const housingUnitSkipKeys = [
      // Add keys that should be skipped entirely
      'stretching', 'compression', 'fillingRatio', 'chamferAngle'
    ]

    function convertByUnitType(parentKey: string, key: string, value: any, imperial: boolean) {
      if (housingUnitSkipKeys.includes(parentKey)) {
        return value
      }
      if (housingUnitConvertAreaKeys.includes(parentKey)) {
        if (typeof value === 'number') {
          // mm² <-> in²
          return imperial ? parseFloat((value / 645.16).toFixed(4)) : parseFloat((value * 645.16).toFixed(2))
        }
      } else if (housingUnitConvertVolumeKeys.includes(parentKey)) {
        if (typeof value === 'number') {
          // mm³ <-> in³
          return imperial ? parseFloat((value / 16387.064).toFixed(4)) : parseFloat((value * 16387.064).toFixed(2))
        }
      } else if (housingUnitConvertMicronKeys.includes(parentKey)) {
        if (typeof value === 'number') {
          // µm <-> µin (1 µm = 39.3701 µin)
          return imperial ? parseFloat((value * 39.3701).toFixed(3)) : parseFloat((value / 39.3701).toFixed(1))
        }
      } else if (housingUnitConvertKeys.includes(key)) {
        if (typeof value === 'number') {
          return convertUnitLength(imperial, value)
        }
      }
      return value
    }

    for (let key in convertedHousing) {
      const attribute = convertedHousing[key]
      // Only iterate if attribute is a non-null object and not an array or string
      if (attribute && typeof attribute === 'object' && !Array.isArray(attribute)) {
        for (let attrKey in attribute) {
          attribute[attrKey] = convertByUnitType(key, attrKey, attribute[attrKey], imperial)
        }
      }
    }
    for (let key in convertedHousing.oRing) {
      const oRingAttribute = convertedHousing.oRing[key]
      if (oRingAttribute && typeof oRingAttribute === 'object' && !Array.isArray(oRingAttribute)) {
        for (let oRingKey in oRingAttribute) {
          oRingAttribute[oRingKey] = convertByUnitType(key, oRingKey, oRingAttribute[oRingKey], imperial)
        }
      }
    }
    for (let key in convertedHousing.catalogueRing) {
      const catalogueRingAttribute = convertedHousing.catalogueRing[key]
      if (catalogueRingAttribute && typeof catalogueRingAttribute === 'object' && !Array.isArray(catalogueRingAttribute)) {
        for (let catalogueKey in catalogueRingAttribute) {
          catalogueRingAttribute[catalogueKey] = convertByUnitType(key, catalogueKey, catalogueRingAttribute[catalogueKey], imperial)
        }
      }
    }

    return convertedHousing
  }

  const convertUnitLength = (imperial: boolean, value: any) => {
    if (value === null) {
      return null
    }

    if (imperial) {
      let inchValue = value / 25.4
      return parseFloat(inchValue.toFixed(4))
    }

    let mmValue = value * 25.4
    return parseFloat(mmValue.toFixed(3))
  }

  function transferToDesignCockpit(isDesignSpace: boolean = false): void {
    // Copy configuration data from Assistant to Design Cockpit
    // Core configuration
    oringDesignCockpitIsHydraulic.value = oringAssistantIsHydraulic.value
    oringDesignCockpitIsRadial.value = oringAssistantIsRadial.value
    oringDesignCockpitIsDynamic.value = oringAssistantIsDynamic.value
    oringDesignCockpitGrooveInShaft.value = oringAssistantGrooveInShaft.value
    oringDesignCockpitOnShaft.value = oringDesignCockpitGrooveInShaft.value ? true : oringAssistantOnShaft.value

    // Set to step 3 directly and mark previous steps as completed
    designCockpitCurrentStep.value = 3
    designCockpitCompletedSteps.value = [1, 2]

    // Transfer materials selection if available
    if (oringMaterialIds.value && oringMaterialIds.value.length > 0) {
      oringBookmarkedMaterialIds.value = [...oringMaterialIds.value]
    }

    let housing
    if (isDesignSpace) {
      housing = oringDesignSpaceCurrentHousing.value
    } else {
      housing = oringAssistantCurrentHousing.value
    }

    oringDesignCockpitCurrentHousing.value = housing ? {...housing} : null

    // Store housing data from the Assistant if available
    if (housing) {
      // Transfer O-Ring dimensions if available
      if (housing.oRing) {
        if (housing.oRing.d1?.val) {
          oringDetailsD1.value = housing.oRing.d1.val
        }
        if (housing.oRing.d2?.val) {
          oringDetailsD2.value = housing.oRing.d2.val
        }
      }

      // Initialize parameter input data if empty
      parameterInputData.value = parameterInputData.value || {}

      // Transfer common parameters across all geometries
      if (housing.r1?.val !== undefined) {
        parameterInputData.value.r1 = housing.r1.val
      }
      if (housing.oRing?.d1?.val !== undefined) {
        parameterInputData.value.d1 = housing.oRing.d1.val
      }
      if (housing.oRing?.d2?.val !== undefined) {
        parameterInputData.value.d2 = housing.oRing.d2.val
      }

      // Geometry-specific parameters
      switch (oringAssistantGeometryId.value) {
      case 8: // Radial, Static - holding
        if (housing.d5?.val !== undefined) {parameterInputData.value.d5 = housing.d5.val}
        if (housing.d10?.val !== undefined) {parameterInputData.value.d10 = housing.d10.val}
        if (housing.d6?.val !== undefined) {parameterInputData.value.d6 = housing.d6.val}
        if (housing.b1?.val !== undefined) {parameterInputData.value.b1in = housing.b1.val}
        break
      case 9: // Radial, Dynamic and Groove in Shaft - stretching
        if (housing.d9?.val !== undefined) {parameterInputData.value.d9 = housing.d9.val}
        if (housing.d4?.val !== undefined) {parameterInputData.value.d4 = housing.d4.val}
        if (housing.d3?.val !== undefined) {parameterInputData.value.d3 = housing.d3.val}
        if (housing.b1?.val !== undefined) {parameterInputData.value.b1out = housing.b1.val}
        break
      case 12: // Axial, Static - both
        if (housing.d8?.val !== undefined) {parameterInputData.value.d8 = housing.d8.val}
        if (housing.h?.val !== undefined) {parameterInputData.value.height = housing.h.val}
        if (housing.b4?.val !== undefined) {parameterInputData.value.b4 = housing.b4.val}
        if (housing.gapWidth?.val !== undefined) {parameterInputData.value.gap = housing.gapWidth.val}
        break
      }

      // Also transfer tolerance class IDs if available
      toleranceData.value = toleranceData.value || {}

      // Transfer common tolerance class IDs
      if (housing.r1?.toleranceClassId) {
        toleranceData.value.r1 = housing.r1.toleranceClassId.toString()
      }
      if (housing.oRing?.d1?.toleranceClassId) {
        toleranceData.value.d1 = housing.oRing.d1.toleranceClassId.toString()
      }
      if (housing.oRing?.d2?.toleranceClassId) {
        toleranceData.value.d2 = housing.oRing.d2.toleranceClassId.toString()
      }

      // Geometry-specific tolerance class IDs
      switch (oringAssistantGeometryId.value) {
      case 8: // Radial, Static - holding
        if (housing.d5?.toleranceClassId) {toleranceData.value.d5 = housing.d5.toleranceClassId.toString()}
        if (housing.d10?.toleranceClassId) {toleranceData.value.d10 = housing.d10.toleranceClassId.toString()}
        if (housing.d6?.toleranceClassId) {toleranceData.value.d6 = housing.d6.toleranceClassId.toString()}
        if (housing.b1?.toleranceClassId) {toleranceData.value.b1in = housing.b1.toleranceClassId.toString()}
        break
      case 9: // Radial, Dynamic and Groove in Shaft - stretching
        if (housing.d9?.toleranceClassId) {toleranceData.value.d9 = housing.d9.toleranceClassId.toString()}
        if (housing.d4?.toleranceClassId) {toleranceData.value.d4 = housing.d4.toleranceClassId.toString()}
        if (housing.d3?.toleranceClassId) {toleranceData.value.d3 = housing.d3.toleranceClassId.toString()}
        if (housing.b1?.toleranceClassId) {toleranceData.value.b1out = housing.b1.toleranceClassId.toString()}
        break
      case 12: // Axial, Static - both
        if (housing.d8?.toleranceClassId) {toleranceData.value.d8 = housing.d8.toleranceClassId.toString()}
        if (housing.h?.toleranceClassId) {toleranceData.value.height = housing.h.toleranceClassId.toString()}
        if (housing.b4?.toleranceClassId) {toleranceData.value.b4 = housing.b4.toleranceClassId.toString()}
        if (housing.gapWidth?.toleranceClassId) {toleranceData.value.gap = housing.gapWidth.toleranceClassId.toString()}
        break
      }
    }

    // Create a fresh housing object in Design Cockpit mode with the transferred data
    // This ensures proper initialization of the housing for the Design Cockpit workflow
    createHousingExpertMode()
  }

  const performTemperatureUnitSwitch = (fahrenheit: boolean) => {
    oringCalculatorFahrenheit.value = fahrenheit
    if (materialAssistantMinTemp.value !== null) {
      materialAssistantMinTemp.value = fahrenheit
        ? parseFloat((materialAssistantMinTemp.value * 9 / 5 + 32).toFixed(2))
        : parseFloat(((materialAssistantMinTemp.value - 32) * 5 / 9).toFixed(2))
    }
    if (materialAssistantMaxTemp.value !== null) {
      materialAssistantMaxTemp.value = fahrenheit
        ? parseFloat((materialAssistantMaxTemp.value * 9 / 5 + 32).toFixed(2))
        : parseFloat(((materialAssistantMaxTemp.value - 32) * 5 / 9).toFixed(2))
    }
  }
  const performPressureSwitch = (psi: boolean) => {
    oringCalculatorPsi.value = psi
  }

  async function loadOringFluidOptions() {
    const {loadFluidsMaterialAssistant} = useOringCalculatorService()
    loadingFluids.value = true
    try {
      // Use German for 'de', English for everything else
      const apiLanguage = currentLanguage.value === 'de' ? 'de' : 'en'

      // Skip reload if we already have data for English and current language is not German
      if (apiLanguage === 'en' &&
          oringMaterialAssistantFluidOptions.value.length > 0 &&
          lastLoadedFluidsLanguage.value === 'en') {
        loadingFluids.value = false
        return
      }

      const result = await loadFluidsMaterialAssistant({
        language: apiLanguage,
        imperial: oringCalculatorImperial.value,
        fahrenheit: oringCalculatorFahrenheit.value
      })
      oringMaterialAssistantFluidOptions.value = Array.isArray(result.data) ? result.data : []
      lastLoadedFluidsLanguage.value = apiLanguage
    } catch {
      oringMaterialAssistantFluidOptions.value = []
    } finally {
      loadingFluids.value = false
    }
  }

  return {
    oringAssistantCurrentHousing,
    oringAssistantIsHydraulic,
    oringAssistantIsRadial,
    oringAssistantIsDynamic,
    oringAssistantApplicationId,
    oringAssistantGeometryId,
    oringAssistantGrooveInShaft,
    oringAssistantOnShaft,
    oringAssistantCheckHousingRequest,
    oringAssistantOptimalORing,
    oringConstructionViewCurrentHousing,
    oringAssistantConstructionView,
    oringCalculatorImperial,
    oringCalculatorFahrenheit,
    oringCalculatorPsi,
    oringAssistantCalculateHousingRequest,
    oringAssistantCalculateOptimalOringRequest,
    oringAssistantSearchParameters,
    oringMaterialIds,
    oringAssistantDownloadPdfRequest,
    oringDesignCockpitDownloadPdfRequest,
    oringAssistantDShaft,
    oringAssistantSendMailRequest,
    oringDesignCockpitSendMailRequest,
    oringDesignCockpitCreateHousingRequest,
    oringDesignCockpitCalculateResult,
    oringDesignCockpitCurrentHousing,
    oringDesignCockpitIsHydraulic,
    oringDesignCockpitIsRadial,
    oringDesignCockpitOnShaft,
    oringDesignCockpitGrooveInShaft,
    oringDesignCockpitIsDynamic,
    oringDesignCockpitCalculateResultRequest,
    oringDesignCockpitApplicationId,
    oringDesignCockpitGeometryId,
    oringDesignCockpitToleranceParameters,
    oringDesignCockpitHasChamfer,
    oringDesignSpaceCurrentHousing,
    oringDesignSpaceD1,
    oringDesignSpaceD2,
    oringDesignSpaceSku,
    oringDesignSpaceCalculateHousingRequest,
    oringDesignSpaceDownloadPdfRequest,
    oringDesignSpaceSendMailRequest,
    currentLanguage,
    setImperial,
    oringSku,
    oringAssistantConstructionViewRequest,
    oringDesignCockpitConstructionViewRequest,
    resetMaterialAssistantStates,
    resetDesignSpaceStates,
    resetAssistantStates,
    resetDesignCockpitStates,
    oringIsAssistantDetails,
    oringDetailsD1,
    oringDetailsD2,
    // Expert Mode Housing Creation
    createHousingExpertMode,
    housingExpertModeResult,
    // Tolerance data
    toleranceClasses,
    toleranceClassesByCategory,
    buildToleranceClassesByCategory,
    toleranceData,
    parameterInputData,
    freeToleranceValues,
    performUnitLengthSwitch,
    getFormattedDate,
    designCockpitCurrentStep,
    getFormattedIsoDateTime,
    oringDesignCockpitDefaultTolerances,
    oringBookmarkedMaterialIds,
    oringPdfData,
    transferToDesignCockpit,
    oringAssistantCurrentStep,
    oringAssistantCompletedSteps,
    resetNavigationState,
    unitSwitchingInProgress,
    designCockpitCompletedSteps,
    // Design Space step navigation
    oringDesignSpaceCurrentStep,
    oringDesignSpaceCompletedSteps,
    initializeChamferAngleWithDefault,
    // Material Assistant states
    oringMaterialAssistantIsDynamic,
    materialAssistantCurrentStep,
    materialAssistantCompletedSteps,
    oringMaterialAssistantMaterialIds,
    oringMaterialAssistantFluidOptions,
    globalLoading,
    oringMaterialSelectionElastomerFilter,
    oringMaterialSelectionShoreHardnessFilter,
    oringMaterialSelectionCompoundFilter,
    oringMaterialAssistantMetricRanges,
    oringMaterialAssistantGapWidth,
    oringMaterialAssistantPressure,
    convertMaterialAssistantPressure,
    materialAssistantMinTemp,
    materialAssistantMaxTemp,
    oringMaterialAssistantFluidIds,
    materialAssistantSourcePage,
    navigateToMaterialAssistant,
    performTemperatureUnitSwitch,
    performPressureSwitch,
    loadOringFluidOptions,
    loadingFluids
  }
})
